import {
  PaymentChannelType,
  ChannelValue,
  ApiError,
  RechargeProductConfig,
  GenerateLinkData
} from '@/services/types';
import {
  PAYMENT_CHANNELS,
  ERROR_MESSAGES,
  VALIDATION_RULES,
  DEBUG_CONSTANTS
} from './constants';

// 类名合并工具函数
export function cn(...classes: (string | undefined | null | false | 0)[]): string {
  return classes.filter(Boolean).join(' ');
}

// 格式化相关辅助函数
export const formatHelpers = {
  /**
   * 格式化价格显示
   * @param price 价格字符串或数字
   * @param currency 货币符号，默认为 ¥
   * @returns 格式化后的价格字符串
   */
  formatPrice(price: string | number, currency = '¥'): string {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    if (isNaN(numPrice)) {
      return `${currency}0.00`;
    }
    
    return `${currency}${numPrice.toFixed(2)}`;
  },

  /**
   * 格式化支付渠道名称
   * @param channel 渠道值
   * @returns 渠道名称
   */
  formatChannelName(channel: ChannelValue): string {
    return PAYMENT_CHANNELS.NAMES[channel] || channel;
  },

  /**
   * 格式化支付类型名称
   * @param type 支付类型
   * @returns 支付类型名称
   */
  formatPaymentTypeName(type: PaymentChannelType): string {
    return PAYMENT_CHANNELS.TYPE_NAMES[type] || type;
  },

  /**
   * 格式化时间戳为可读时间
   * @param timestamp 时间戳（秒或毫秒）
   * @param format 格式类型
   * @returns 格式化后的时间字符串
   */
  formatTimestamp(
    timestamp: number,
    format: 'datetime' | 'date' | 'time' | 'relative' = 'datetime'
  ): string {
    // 判断是秒还是毫秒
    const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    
    if (isNaN(date.getTime())) {
      return '无效时间';
    }
    
    switch (format) {
      case 'date':
        return date.toLocaleDateString('zh-CN');
      case 'time':
        return date.toLocaleTimeString('zh-CN');
      case 'relative':
        return formatHelpers.formatRelativeTime(date);
      case 'datetime':
      default:
        return date.toLocaleString('zh-CN');
    }
  },

  /**
   * 格式化相对时间
   * @param date 日期对象
   * @returns 相对时间字符串
   */
  formatRelativeTime(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return '刚刚';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}分钟前`;
    } else if (diffHours < 24) {
      return `${diffHours}小时前`;
    } else if (diffDays < 7) {
      return `${diffDays}天前`;
    } else {
      return date.toLocaleDateString('zh-CN');
    }
  },

  /**
   * 格式化文件大小
   * @param bytes 字节数
   * @returns 格式化后的文件大小字符串
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }
};

// 验证相关辅助函数
export const validationHelpers = {
  /**
   * 验证 UUID 格式
   * @param uuid UUID 字符串
   * @returns 是否为有效的 UUID
   */
  isValidUUID(uuid: string): boolean {
    return VALIDATION_RULES.UUID_REGEX.test(uuid);
  },

  /**
   * 验证价格格式
   * @param price 价格字符串
   * @returns 是否为有效的价格格式
   */
  isValidPrice(price: string): boolean {
    return VALIDATION_RULES.PRICE_REGEX.test(price);
  },

  /**
   * 验证支付渠道
   * @param channel 渠道值
   * @returns 是否为有效的支付渠道
   */
  isValidChannel(channel: string): channel is ChannelValue {
    return VALIDATION_RULES.VALID_CHANNELS.includes(channel as ChannelValue);
  },

  /**
   * 验证支付类型
   * @param type 支付类型
   * @returns 是否为有效的支付类型
   */
  isValidPaymentType(type: string): type is PaymentChannelType {
    return VALIDATION_RULES.VALID_PAYMENT_TYPES.includes(type as PaymentChannelType);
  },

  /**
   * 验证字符串长度
   * @param str 字符串
   * @param min 最小长度
   * @param max 最大长度
   * @returns 是否在有效长度范围内
   */
  isValidStringLength(str: string, min: number, max: number): boolean {
    return str.length >= min && str.length <= max;
  },

  /**
   * 验证充值产品配置
   * @param config 充值产品配置
   * @returns 验证结果
   */
  validateRechargeConfig(config: RechargeProductConfig): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!validationHelpers.isValidUUID(config.recharge_product_id)) {
      errors.push('充值产品ID格式无效');
    }

    if (!validationHelpers.isValidStringLength(
      config.recharge_product_name,
      VALIDATION_RULES.STRING_LIMITS.MIN_NAME_LENGTH,
      VALIDATION_RULES.STRING_LIMITS.MAX_NAME_LENGTH
    )) {
      errors.push('产品名称长度无效');
    }

    if (!validationHelpers.isValidPrice(config.cny_price)) {
      errors.push('价格格式无效');
    }

    const channels = [''];

    for (const channel of channels) {
      if (!validationHelpers.isValidChannel(channel)) {
        errors.push(`无效的渠道值: ${channel}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

// 错误处理相关辅助函数
export const errorHelpers = {
  /**
   * 获取错误消息
   * @param error API 错误对象
   * @returns 用户友好的错误消息
   */
  getErrorMessage(error: ApiError): string {
    if (error.message) {
      return error.message;
    }

    const message = ERROR_MESSAGES[error.error_code as keyof typeof ERROR_MESSAGES];
    return message || ERROR_MESSAGES.DEFAULT;
  },

  /**
   * 判断是否为网络错误
   * @param error 错误对象
   * @returns 是否为网络错误
   */
  isNetworkError(error: any): boolean {
    return (
      error instanceof TypeError ||
      error.name === 'NetworkError' ||
      error.message?.includes('fetch') ||
      error.message?.includes('network')
    );
  },

  /**
   * 判断是否为超时错误
   * @param error 错误对象
   * @returns 是否为超时错误
   */
  isTimeoutError(error: any): boolean {
    return (
      error.name === 'TimeoutError' ||
      error.name === 'AbortError' ||
      error.message?.includes('timeout')
    );
  },

  /**
   * 创建标准化的错误对象
   * @param message 错误消息
   * @param code 错误码
   * @param details 错误详情
   * @returns 标准化的 API 错误对象
   */
  createApiError(message: string, code = -1, details?: any): ApiError {
    return {
      error_code: code,
      message,
      details
    };
  }
};

// 数据处理相关辅助函数
export const dataHelpers = {
  /**
   * 深拷贝对象
   * @param obj 要拷贝的对象
   * @returns 深拷贝后的对象
   */
  deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj.getTime()) as unknown as T;
    }

    if (obj instanceof Array) {
      return obj.map(item => dataHelpers.deepClone(item)) as unknown as T;
    }

    if (typeof obj === 'object') {
      const cloned = {} as T;
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          cloned[key] = dataHelpers.deepClone(obj[key]);
        }
      }
      return cloned;
    }

    return obj;
  },

  /**
   * 安全的 JSON 解析
   * @param jsonString JSON 字符串
   * @param defaultValue 默认值
   * @returns 解析后的对象或默认值
   */
  safeJsonParse<T>(jsonString: string, defaultValue: T): T {
    try {
      return JSON.parse(jsonString);
    } catch {
      return defaultValue;
    }
  },

  /**
   * 安全的 JSON 序列化
   * @param obj 要序列化的对象
   * @param defaultValue 默认值
   * @returns JSON 字符串或默认值
   */
  safeJsonStringify(obj: any, defaultValue = '{}'): string {
    try {
      return JSON.stringify(obj);
    } catch {
      return defaultValue;
    }
  },

  /**
   * 过滤对象中的空值
   * @param obj 对象
   * @returns 过滤后的对象
   */
  filterEmptyValues<T extends Record<string, any>>(obj: T): Partial<T> {
    const filtered: Partial<T> = {};
    
    for (const key in obj) {
      const value = obj[key];
      if (value !== null && value !== undefined && value !== '') {
        filtered[key] = value;
      }
    }
    
    return filtered;
  },

  /**
   * 合并对象（深度合并）
   * @param target 目标对象
   * @param sources 源对象数组
   * @returns 合并后的对象
   */
  deepMerge<T extends Record<string, any>>(target: T, ...sources: Partial<T>[]): T {
    if (!sources.length) return target;
    
    const source = sources.shift();
    if (!source) return target;

    if (dataHelpers.isObject(target) && dataHelpers.isObject(source)) {
      for (const key in source) {
        if (dataHelpers.isObject(source[key])) {
          if (!target[key]) {
            (target as any)[key] = {};
          }
          dataHelpers.deepMerge(target[key], source[key] as any);
        } else {
          (target as any)[key] = source[key];
        }
      }
    }

    return dataHelpers.deepMerge(target, ...sources);
  },

  /**
   * 判断是否为对象
   * @param item 要判断的项
   * @returns 是否为对象
   */
  isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }
};

// URL 相关辅助函数
export const urlHelpers = {
  /**
   * 构建查询字符串
   * @param params 参数对象
   * @returns 查询字符串
   */
  buildQueryString(params: Record<string, any>): string {
    const filtered = dataHelpers.filterEmptyValues(params);
    const searchParams = new URLSearchParams();
    
    for (const [key, value] of Object.entries(filtered)) {
      searchParams.append(key, String(value));
    }
    
    return searchParams.toString();
  },

  /**
   * 解析查询字符串
   * @param queryString 查询字符串
   * @returns 参数对象
   */
  parseQueryString(queryString: string): Record<string, string> {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(queryString);
    
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    
    return params;
  },

  /**
   * 验证 URL 格式
   * @param url URL 字符串
   * @returns 是否为有效的 URL
   */
  isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * 获取域名
   * @param url URL 字符串
   * @returns 域名或空字符串
   */
  getDomain(url: string): string {
    try {
      return new URL(url).hostname;
    } catch {
      return '';
    }
  }
};

// 性能相关辅助函数
export const performanceHelpers = {
  /**
   * 防抖函数
   * @param func 要防抖的函数
   * @param delay 延迟时间（毫秒）
   * @returns 防抖后的函数
   */
  debounce<T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let timeoutId: number;
    
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => func(...args), delay);
    };
  },

  /**
   * 节流函数
   * @param func 要节流的函数
   * @param delay 延迟时间（毫秒）
   * @returns 节流后的函数
   */
  throttle<T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let lastCall = 0;
    
    return (...args: Parameters<T>) => {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        func(...args);
      }
    };
  },

  /**
   * 延迟执行
   * @param ms 延迟时间（毫秒）
   * @returns Promise
   */
  sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  },

  /**
   * 测量函数执行时间
   * @param func 要测量的函数
   * @param label 标签
   * @returns 函数执行结果
   */
  async measureTime<T>(func: () => Promise<T>, label = 'Function'): Promise<T> {
    const start = performance.now();
    const result = await func();
    const end = performance.now();
    
    if (DEBUG_CONSTANTS.FLAGS.ENABLE_PERFORMANCE_LOGS) {
      console.log(`${label} 执行时间: ${(end - start).toFixed(2)}ms`);
    }
    
    return result;
  }
};

// 支付链接相关辅助函数
export const paymentHelpers = {
  /**
   * 检查支付链接是否过期
   * @param data 支付链接数据
   * @returns 是否过期
   */
  isPaymentLinkExpired(data: GenerateLinkData): boolean {
    if (!data.expire_time) {
      return false; // 没有过期时间，认为不过期
    }
    
    return Date.now() > data.expire_time * 1000; // expire_time 是秒，需要转换为毫秒
  },

  /**
   * 获取支付链接剩余有效时间（秒）
   * @param data 支付链接数据
   * @returns 剩余时间（秒）
   */
  getPaymentLinkRemainingTime(data: GenerateLinkData): number {
    if (!data.expire_time) {
      return Infinity; // 没有过期时间，返回无限大
    }
    
    const remaining = data.expire_time - Math.floor(Date.now() / 1000);
    return Math.max(0, remaining);
  },

  /**
   * 格式化剩余时间为可读字符串
   * @param seconds 剩余秒数
   * @returns 格式化后的时间字符串
   */
  formatRemainingTime(seconds: number): string {
    if (seconds === Infinity) {
      return '永不过期';
    }
    
    if (seconds <= 0) {
      return '已过期';
    }
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}分${remainingSeconds}秒`;
    }
    
    return `${remainingSeconds}秒`;
  },

  /**
   * 生成支付二维码数据
   * @param paymentUrl 支付链接
   * @returns 二维码数据对象
   */
  generateQRCodeData(paymentUrl: string): {
    url: string;
    size: number;
    errorCorrectionLevel: 'L' | 'M' | 'Q' | 'H';
  } {
    return {
      url: paymentUrl,
      size: 200,
      errorCorrectionLevel: 'M'
    };
  },

  /**
   * 复制支付链接到剪贴板
   * @param paymentUrl 支付链接
   * @returns 是否复制成功
   */
  async copyPaymentLink(paymentUrl: string): Promise<boolean> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(paymentUrl);
        return true;
      } else {
        // 降级方案
        const textArea = document.createElement('textarea');
        textArea.value = paymentUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        return success;
      }
    } catch (error) {
      console.error('复制支付链接失败:', error);
      return false;
    }
  }
};

// 调试相关辅助函数
export const debugHelpers = {
  /**
   * 条件日志输出
   * @param level 日志级别
   * @param message 消息
   * @param data 数据
   */
  log(level: 'error' | 'warn' | 'info' | 'debug', message: string, data?: any): void {
    if (!DEBUG_CONSTANTS.FLAGS.ENABLE_API_LOGS) return;

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    switch (level) {
      case 'error':
        console.error(prefix, message, data);
        break;
      case 'warn':
        console.warn(prefix, message, data);
        break;
      case 'info':
        console.info(prefix, message, data);
        break;
      case 'debug':
        console.debug(prefix, message, data);
        break;
    }
  },

  /**
   * 性能标记开始
   * @param name 标记名称
   */
  markStart(name: string): void {
    if (DEBUG_CONSTANTS.FLAGS.ENABLE_PERFORMANCE_LOGS) {
      performance.mark(`${name}-start`);
    }
  },

  /**
   * 性能标记结束并输出耗时
   * @param name 标记名称
   */
  markEnd(name: string): void {
    if (DEBUG_CONSTANTS.FLAGS.ENABLE_PERFORMANCE_LOGS) {
      performance.mark(`${name}-end`);
      performance.measure(name, `${name}-start`, `${name}-end`);
      
      const measure = performance.getEntriesByName(name, 'measure')[0];
      if (measure) {
        console.log(`${name} 耗时: ${measure.duration.toFixed(2)}ms`);
      }
    }
  },

  /**
   * 输出对象的详细信息
   * @param obj 对象
   * @param label 标签
   */
  inspect(obj: any, label = 'Object'): void {
    if (DEBUG_CONSTANTS.FLAGS.ENABLE_API_LOGS) {
      console.group(`🔍 ${label} 详细信息`);
      console.log('类型:', typeof obj);
      console.log('值:', obj);
      if (obj && typeof obj === 'object') {
        console.log('键:', Object.keys(obj));
        console.log('JSON:', dataHelpers.safeJsonStringify(obj, '无法序列化'));
      }
      console.groupEnd();
    }
  }
};

// 浏览器兼容性辅助函数
export const browserHelpers = {
  /**
   * 检查是否支持 localStorage
   * @returns 是否支持
   */
  supportsLocalStorage(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * 检查是否支持 sessionStorage
   * @returns 是否支持
   */
  supportsSessionStorage(): boolean {
    try {
      const test = '__sessionStorage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * 检查是否支持 fetch API
   * @returns 是否支持
   */
  supportsFetch(): boolean {
    return typeof fetch !== 'undefined';
  },

  /**
   * 检查是否支持 Promise
   * @returns 是否支持
   */
  supportsPromise(): boolean {
    return typeof Promise !== 'undefined';
  },

  /**
   * 获取浏览器信息
   * @returns 浏览器信息对象
   */
  getBrowserInfo(): {
    userAgent: string;
    language: string;
    platform: string;
    cookieEnabled: boolean;
    onLine: boolean;
  } {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine
    };
  }
};

// 导出所有辅助函数
export default {
  format: formatHelpers,
  validation: validationHelpers,
  error: errorHelpers,
  data: dataHelpers,
  url: urlHelpers,
  performance: performanceHelpers,
  payment: paymentHelpers,
  debug: debugHelpers,
  browser: browserHelpers
};